import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'

interface WinnerRow {
  season: number
  major: string
  player_name: string
}

const MAJOR_ORDER = ['masters', 'pga', 'us_open', 'the_open'] as const
const MAJOR_SHORT: Record<string, string> = {
  masters: 'Masters',
  pga: 'PGA',
  us_open: 'US Open',
  the_open: 'The Open',
}

/**
 * Segrare per major — den enda historiska uppgift som är avstämd rad för
 * rad, och den som visas överst på maratonsidan.
 *
 * Rutnätet fylls på här allteftersom en major avgörs. Ingen egen
 * databasfunktion behövs: policyn på archive_major_winners ger redan
 * is_admin() insert, update och delete.
 *
 * Namnen väljs ur listan i stället för att skrivas fritt. Främmande
 * nyckeln avvisar ändå en felstavning, men ett avvisat sparande är en
 * sämre upplevelse än att inte kunna stava fel — och det var så nära
 * "Hasse Djup" och "Hans Djup" var att bli två personer i statistiken.
 * Behövs en ny deltagare läggs hen till uttryckligen.
 */
export function AdminWinners() {
  const { profile } = useAuth()
  const [winners, setWinners] = useState<WinnerRow[]>([])
  const [names, setNames] = useState<string[]>([])
  const [saving, setSaving] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const [nyDeltagare, setNyDeltagare] = useState('')
  const [nySasong, setNySasong] = useState('')
  const [extraSasonger, setExtraSasonger] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [w, n] = await Promise.all([
      supabase.from('archive_major_winners')
        .select('season, major, player_name')
        .order('season', { ascending: false }),
      supabase.from('archive_major_winner_names')
        .select('player_name')
        .order('player_name'),
    ])
    if (w.error) setError(w.error.message)
    else setWinners((w.data as WinnerRow[]) ?? [])
    if (n.error) setError(n.error.message)
    else setNames(((n.data as Array<{ player_name: string }>) ?? []).map((x) => x.player_name))
    setLoading(false)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (profile && profile.role !== 'admin') {
    return (
      <div className="page">
        <div className="notice notice--error">Den här sidan är bara för admin.</div>
      </div>
    )
  }

  // Säsonger som redan har en segrare, plus de man lagt till i den här
  // sessionen. En tom säsong finns inte i databasen — den blir till först
  // när den första rutan fylls i.
  const sasonger = [...new Set([...winners.map((w) => w.season), ...extraSasonger])]
    .sort((a, b) => b - a)

  function nyckel(season: number, major: string) {
    return `${season}:${major}`
  }

  async function satt(season: number, major: string, playerName: string) {
    const k = nyckel(season, major)
    setSaving(k)
    setError(null)

    // Tomt val betyder "ingen segrare registrerad", alltså att raden ska
    // bort. Att spara en tom sträng hade brutit mot främmande nyckeln.
    const { error } = playerName
      ? await supabase
          .from('archive_major_winners')
          .upsert({ season, major, player_name: playerName }, { onConflict: 'season,major' })
      : await supabase
          .from('archive_major_winners')
          .delete()
          .eq('season', season)
          .eq('major', major)

    setSaving(null)

    if (error) {
      setError(
        error.code === '23503'
          ? `${playerName} finns inte i deltagarlistan. Lägg till namnet först.`
          : error.message,
      )
      return
    }

    setWinners((prev) => {
      const utan = prev.filter((w) => !(w.season === season && w.major === major))
      return playerName ? [...utan, { season, major, player_name: playerName }] : utan
    })
    setSaved(k)
    window.setTimeout(() => setSaved((s) => (s === k ? null : s)), 1600)
  }

  async function laggTillDeltagare() {
    const namn = nyDeltagare.trim()
    if (!namn) return
    setError(null)

    const { error } = await supabase
      .from('archive_major_winner_names')
      .insert({ player_name: namn })

    if (error) {
      setError(error.code === '23505' ? `${namn} finns redan i listan.` : error.message)
      return
    }
    setNames((prev) => [...prev, namn].sort((a, b) => a.localeCompare(b, 'sv')))
    setNyDeltagare('')
  }

  function laggTillSasong() {
    const ar = Number(nySasong)
    if (!Number.isInteger(ar) || ar < 2000 || ar > 2100) {
      setError('Ange ett årtal mellan 2000 och 2100.')
      return
    }
    setError(null)
    setExtraSasonger((prev) => [...new Set([...prev, ar])])
    setNySasong('')
  }

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">Admin</p>
        <h1>Segrare per major</h1>
      </div>

      <div>
        <Link to="/admin"><button>Turneringar</button></Link>{' '}
        <Link to="/admin/anvandare"><button>Deltagare</button></Link>
      </div>

      {error && <div className="notice notice--error">{error}</div>}
      {loading && <p className="muted">Hämtar…</p>}

      {!loading && (
        <>
          <div className="card stack">
            <p className="muted">
              Rutnätet som står överst på maratonsidan. Välj segrare i rutan — det
              sparas direkt. Tomt val tar bort segraren för den majorn.
            </p>

            <div className="winners">
              <div className="winners__row winners__row--head">
                <span className="winners__season" />
                {MAJOR_ORDER.map((m) => (
                  <span key={m} className="winners__cell">{MAJOR_SHORT[m]}</span>
                ))}
              </div>

              {sasonger.map((s) => (
                <div key={s} className="winners__row">
                  <span className="winners__season">{s}</span>
                  {MAJOR_ORDER.map((m) => {
                    const k = nyckel(s, m)
                    const nu = winners.find((w) => w.season === s && w.major === m)
                    return (
                      <span key={m} className="winners__cell">
                        <select
                          value={nu?.player_name ?? ''}
                          disabled={saving === k}
                          onChange={(e) => void satt(s, m, e.target.value)}
                        >
                          <option value="">—</option>
                          {names.map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                        {saving === k && <span className="muted"> sparar…</span>}
                        {saved === k && <span className="muted"> sparat</span>}
                      </span>
                    )
                  })}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ maxWidth: 160 }}>
                <label htmlFor="nysasong">Lägg till säsong</label>
                <input
                  id="nysasong"
                  type="number"
                  value={nySasong}
                  placeholder="2027"
                  onChange={(e) => setNySasong(e.target.value)}
                />
              </div>
              <button onClick={laggTillSasong}>Lägg till</button>
            </div>
          </div>

          <div className="card stack">
            <h2>Deltagare</h2>
            <p className="muted">
              Bara namn som står här går att välja som segrare. Spärren finns för att
              samma person inte ska hamna i statistiken under två stavningar.
            </p>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label htmlFor="nydeltagare">Nytt namn</label>
                <input
                  id="nydeltagare"
                  value={nyDeltagare}
                  placeholder="Förnamn Efternamn"
                  onChange={(e) => setNyDeltagare(e.target.value)}
                />
              </div>
              <button onClick={() => void laggTillDeltagare()}>Lägg till</button>
            </div>

            <p className="muted">{names.join(' · ')}</p>
          </div>
        </>
      )}
    </div>
  )
}
